// src/features/analytics/components/MediaPeaksPanel.tsx
//
// "Peak viewers per day" — compact per-media daily peak-concurrency trend
// inside the Content-tab Drawer (metrics expansion). Renders for both live
// and vod rows. Follows PlaybackTrendCard conventions: UTC day axis, honest
// zero-fill via fillableRange (never future or pre-coverage days), custom
// tooltip Paper, coverage notice, honest empty state.

import { Paper, Skeleton, Text, Title } from '@mantine/core';
import { AreaChart } from '@mantine/charts';
import { DateTime } from 'luxon';
import type { UtcRange } from '@/shared/utils/utcRange';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { fillPeakDayGaps, type PeakChartDay } from '../mediaPeaks';
import { fillableRange, trendTickInterval, utcDayLabel } from '../playbackTrend';
import { AnalyticsCoverageNotice } from './AnalyticsCoverageNotice';
import { AnalyticsErrorState } from './AnalyticsErrorState';
import type { AnalyticsEnvelope, MediaPeaksData } from '../types';

interface MediaPeaksPanelProps {
  peaks: AnalyticsEnvelope<MediaPeaksData> | null;
  range: UtcRange;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onRetry: () => void;
}

interface PeakChartRow {
  day: string;
  peak: number;
  peakMinute: number | null;
  provisional: boolean;
}

/** Peak minutes are points in time — rendered in the viewer's LOCAL timezone (ADR-003 §4). */
function peakMinuteLabel(seconds: number): string {
  return DateTime.fromSeconds(seconds).toFormat('MMM d, h:mm a');
}

function PeaksTooltip({ label, payload }: { label?: unknown; payload?: unknown }) {
  const rows = payload as { payload?: PeakChartRow }[] | undefined;
  const row = rows?.[0]?.payload;
  if (!row) return null;
  return (
    <Paper withBorder shadow="sm" p="xs">
      <Text size="xs" fw={600}>
        {String(label)} (UTC)
      </Text>
      <Text size="xs">Peak viewers: {AnalyticsUtils.formatCount(row.peak)}</Text>
      {row.peakMinute !== null && (
        <Text size="xs" c="dimmed">
          at {peakMinuteLabel(row.peakMinute)}
          {row.provisional && ' (provisional)'}
        </Text>
      )}
    </Paper>
  );
}

export function MediaPeaksPanel({
  peaks,
  range,
  isLoading,
  isError,
  error,
  onRetry,
}: MediaPeaksPanelProps) {
  const rows = peaks?.data.rows ?? [];
  const filled: PeakChartDay[] = fillPeakDayGaps(
    rows,
    fillableRange(range, peaks?.coverage, Math.floor(Date.now() / 1000))
  );
  const chartData: PeakChartRow[] = filled.map((d) => ({
    day: utcDayLabel(d.day),
    peak: d.peakConcurrent,
    peakMinute: d.peakMinute,
    provisional: d.provisional,
  }));

  return (
    <Paper withBorder p="md">
      <Title order={5} mb="xs">
        Peak viewers per day
      </Title>
      {isLoading ? (
        <Skeleton height={200} />
      ) : isError ? (
        <AnalyticsErrorState error={error} onRetry={onRetry} />
      ) : rows.length === 0 ? (
        <AnalyticsCoverageNotice
          coverage={peaks?.coverage}
          freshness={peaks?.freshness}
          emptyMessage="No concurrency data for this media in the selected range."
        />
      ) : (
        <>
          <AnalyticsCoverageNotice coverage={peaks?.coverage} freshness={peaks?.freshness} />
          <AreaChart
            h={200}
            data={chartData}
            dataKey="day"
            series={[{ name: 'peak', label: 'Peak viewers', color: 'blue.6' }]}
            curveType="monotone"
            withDots={false}
            xAxisProps={{ interval: trendTickInterval(filled.length) }}
            tooltipProps={{
              content: ({ label, payload }) => <PeaksTooltip label={label} payload={payload} />,
            }}
          />
          <Text size="xs" c="dimmed" mt={4}>
            Daily peak concurrent viewers per UTC day (peak times shown in your local timezone).
          </Text>
        </>
      )}
    </Paper>
  );
}
